/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { Result } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { processSellSwapAction } from '@root/features/trade/swap'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { tradingWalletAtom } from '@root/features/trade/trading'
import { reatomMarketSellControls } from '@root/features/trade/trading-controls/atoms/common/sell'
import { Error } from '@root/shared'

import { reatomInstantSellQuote } from './reatom-instant-sell-quote'

import type { PairModel } from '@root/entities/pair/atoms'

export const reatomInstantSell = ({ pair }: { pair: PairModel }) => {
  const pairAtom = atom(pair)
  const marketModel = reatomMarketSellControls({
    pairAtom: pairAtom,
    walletAtom: tradingWalletAtom,
  })
  const quotesModel = reatomInstantSellQuote({ pair })

  const quickSellAction = action(async (ctx, percentsFraction: number) => {
    const wallet = ctx.get(tradingWalletAtom)
    const settings = ctx.get(tradeSettingsAtom)

    if (!percentsFraction || !wallet) {
      return Result.fail(Error.UnknownError.fromUnknown('invalid input'))
    }

    const asset = ctx.get(wallet.assetsByCurrency)[pair.quote.address]
    if (!asset) {
      return
    }
    const balance = ctx.get(asset.balance)
    if (balance.isZero()) {
      return
    }
    const amount = balance.multipliedBy(percentsFraction)

    const quoteModel = ctx
      .get(quotesModel)
      .find((quote) => quote.amount.eq(amount))?.quoteModel
    const quote = quoteModel ? ctx.get(quoteModel.quote) : undefined

    return processSellSwapAction(ctx, {
      percentage: percentsFraction,
      pair: pair,
      wallet: wallet,
      settings: settings,
      quote,
    })
  }, 'quickSellAction')

  const initializeAction = action((ctx) => {
    quotesModel.subscribe(ctx)
    return () => {
      quotesModel.unsubscribe(ctx)
    }
  })

  return { ...marketModel, quickSellAction, initialize: initializeAction }
}

export type InstantSellQuickOptions = readonly [BigNumber, BigNumber, BigNumber]

export const INSTANT_SELL_QUICK_OPTIONS: InstantSellQuickOptions = [
  BigNumber(25),
  BigNumber(50),
  BigNumber(100),
]

export const instantSellQuickOptionsAtom = atom(
  (ctx): InstantSellQuickOptions => {
    const user = ctx.spy(userAtom)

    return INSTANT_SELL_QUICK_OPTIONS.map((x, i) => {
      return user?.instantTradeButtons.sellButtons[i]?.percent ?? x
    }) as unknown as InstantSellQuickOptions
  },
  'instantSellQuickOptionsAtom',
)
